/// <amd-module name="Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaService" />
import {
    IObservableArray,
    IObservableValue,
    IReadyableObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FilterClause } from "Wit/Clients/Work/Contracts/Work";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getInvariantOperator, getLocalizedOperator } from "Wit/Common/WiqlOperators";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { getListBoxItemsFromFields, getSupportedFieldDefinitions } from "../../Utils/FieldUtils";
import { CriteriaConstants } from "./CriteriaConstants";
import { FilterClauseConstants, ICriteriaSelectedSettingData, ICriteriaSettingData } from "./CriteriaInterfaces";

export function getFieldCriteriaAsFilterClauses(clauses: ICriteriaSelectedSettingData[]): FilterClause[] {
    return clauses.map((setting, index) => {
        return {
            index: index,
            fieldName: setting.field,
            logicalOperator: FilterClauseConstants.andOperator,
            operator: getInvariantOperator(setting.operator),
            value: getInvariantOperator(setting.criteriaValue.value)
        };
    });
}

export function getCriteriaSelectedSettingData(criteria: FilterClause[]): ICriteriaSelectedSettingData[] {
    return criteria.map(c => {
        return {
            criteriaValue: new ObservableValue(c.value),
            field: c.fieldName,
            operator: c.operator
        };
    });
}

export interface ICriteriaService extends IVssService {
    /**
     * Error message from data provider failure
     */
    dataProviderErrorMessage: IObservableValue<string | undefined>;

    /**
     * Observable that indicates when field values have been loaded
     */
    fieldsReady: IObservableValue<boolean>;

    /**
     * Tracks if data has changed if teams were initially supplied
     */
    isDirty: IObservableValue<boolean>;

    /**
     * Tracks if data is valid
     */
    isValid: IObservableValue<boolean>;

    /**
     * Observable list of configured criteria settings
     */
    rows: IObservableArray<ICriteriaSettingData>;

    /**
     * Error message from invalid criteria settings
     */
    validationErrorMessage: IObservableValue<string | undefined>;

    /**
     * Async add a new criteria settings row with default values
     */
    addCriteriaSettingsRow(criteriaSetting?: ICriteriaSelectedSettingData): Promise<void>;

    /**
     * Add existing team settings rows
     *
     * Note: this is intended to be used a single time when the component is passed existing team data
     */
    addCriteriaSettingsRows(criteriaSettings: ICriteriaSelectedSettingData[]): void;

    /**
     * Clear the current criteria settings
     */
    clear(): void;

    /**
     * Remove a criteria settings entry
     */
    removeCriteriaRow(index: number): void;

    /**
     * Update the field of a criteria setting
     */
    updateField(index: number, fieldReferenceName: string, preserveSelection?: boolean): Promise<void>;

    /**
     * Update the operator of a criteria setting
     */
    updateOperator(index: number, operator: string, skipValidation?: boolean): void;

    /**
     * Update the value of a criteria setting
     */
    updateValue(index: number, value: string, skipValidation?: boolean): void;
}

class CriteriaService extends VssService implements ICriteriaService {
    private _dataProviderErrorMessage: IObservableValue<string | undefined>;
    private _isDirty: IObservableValue<boolean>;
    private _isValid: IObservableValue<boolean>;
    private _rows: IObservableArray<ICriteriaSettingData>;
    private _validationErrorMessage: IObservableValue<string | undefined>;

    private _initialized: Promise<void>;
    private _defaultField: IFieldEntry;
    private _fieldListBoxItems: IReadyableObservableArray<IListBoxItem>;
    private _useNewWorkItemFieldService: boolean;
    private _fieldService: IWorkItemFieldService;
    private _fieldService2: IWorkItemFieldService2;
    private _operators: { [fieldReferenceName: string]: string[] };
    private _originalCriteria: ICriteriaSelectedSettingData[] | undefined;

    public get dataProviderErrorMessage(): IObservableValue<string | undefined> {
        return this._dataProviderErrorMessage;
    }

    public get fieldsReady(): IObservableValue<boolean> {
        return this._fieldListBoxItems.ready;
    }

    public get isDirty(): IObservableValue<boolean> {
        return this._isDirty;
    }

    public get isValid(): IObservableValue<boolean> {
        return this._isValid;
    }

    public get rows(): IObservableArray<ICriteriaSettingData> {
        return this._rows;
    }

    public get validationErrorMessage(): IObservableValue<string | undefined> {
        return this._validationErrorMessage;
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this._dataProviderErrorMessage = new ObservableValue<string | undefined>(undefined);
        this._isDirty = new ObservableValue<boolean>(false);
        this._isValid = new ObservableValue<boolean>(true);
        this._rows = new ObservableArray([]);
        this._validationErrorMessage = new ObservableValue<string | undefined>(undefined);

        this._fieldListBoxItems = new ReadyableObservableArray([]);
        this._useNewWorkItemFieldService = isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false);
        if (this._useNewWorkItemFieldService) {
            this._fieldService2 = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
        } else {
            this._fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        }
        this._operators = {};

        this._initialized = this._initialize();
    }

    public async addCriteriaSettingsRow(criteriaSetting?: ICriteriaSelectedSettingData) {
        await this._initialized;
        const newRow = this._getDefaultCriteria(criteriaSetting);
        this._rows.push(newRow);
        const index = this._rows.length - 1;

        // this will only make async fetch when the first row is added
        await this.updateField(index, newRow.field, !!criteriaSetting);
    }

    public async addCriteriaSettingsRows(criteriaSettings: ICriteriaSelectedSettingData[]) {
        // save criteria for dirty comparisons
        this._originalCriteria = criteriaSettings;
        criteriaSettings.forEach(criteria => this.addCriteriaSettingsRow(criteria));
    }

    public clear() {
        this._rows.value = [];
        this._validationErrorMessage.value = undefined;
        this._dataProviderErrorMessage.value = undefined;
        this._isValid.value = true;
        this._isDirty.value = false;
    }

    public removeCriteriaRow(rowIndex: number) {
        const rows = this._rows.value;
        rows.splice(rowIndex, 1);
        this._rows.value = rows;

        // removing a row can't put model into an error state
        this._validateCriteria();
    }

    public async updateField(index: number, fieldReferenceName: string, preserveSelection?: boolean) {
        // make sure field data is loaded
        await this._initialized;
        const field = this._useNewWorkItemFieldService 
                    ? this._fieldService2.getFieldByReferenceName(fieldReferenceName)
                    : this._fieldService.getFieldByReferenceName(fieldReferenceName);
        if (!field) {
            return;
        }

        const criteriaSettingsData = this._rows.value[index];
        const fieldIndex = criteriaSettingsData.fields.value.findIndex(item => item.id === fieldReferenceName);
        criteriaSettingsData.field = fieldReferenceName;
        criteriaSettingsData.fieldSelection.select(fieldIndex);

        // clear selections
        criteriaSettingsData.operatorSelection.clear();

        // update operator options
        const operatorsForField = this._getOperators(field);
        let indexToSelect = 0;
        if (preserveSelection) {
            const operatorIndex = operatorsForField.findIndex(operator => operator === criteriaSettingsData.operator);
            indexToSelect = operatorIndex >= 0 ? operatorIndex : indexToSelect;
        }

        const operator = operatorsForField[indexToSelect];
        this.updateOperator(index, operator, /** skipValidation */ true);
        criteriaSettingsData.operators.value = this._getListBoxItems(operatorsForField);
        criteriaSettingsData.operatorSelection.select(indexToSelect);

        // update the value and allowed values
        !preserveSelection && this.updateValue(index, "", /** skipValidation */ true);
        await this._updateAllowedValues(criteriaSettingsData);

        this._validateCriteria();
    }

    public updateOperator(index: number, operator: string, skipValidation?: boolean) {
        const criteriaSettingsData = this._rows.value[index];
        criteriaSettingsData.operator = operator;
        !skipValidation && this._validateCriteria();
    }

    public updateValue(index: number, value: string, skipValidation?: boolean) {
        const criteriaSettingsData = this._rows.value[index];
        criteriaSettingsData.criteriaValue.value = value;
        !skipValidation && this._validateCriteria();
    }

    private async _initialize() {
        const fields = this._useNewWorkItemFieldService 
                    ? await this._fieldService2.loadFields()
                    : await this._fieldService.getFieldsAsync();

        const notSupportedFieldReferenceNames = CriteriaConstants.notSupportedFieldReferenceNames;
        notSupportedFieldReferenceNames.delete(CoreFieldRefNames.Parent.toLowerCase());

        if (fields) {
            const supportedFields = getSupportedFieldDefinitions(
                fields,
                CriteriaConstants.supportedFieldTypes,
                notSupportedFieldReferenceNames
            ).filter(field => !field.isIdentity); // identity fields are not supported yet
            this._fieldListBoxItems.value = getListBoxItemsFromFields(supportedFields);
            this._fieldListBoxItems.ready.value = true;
            this._defaultField = supportedFields[0];
            this._getOperators(this._defaultField);
        } else {
            this._dataProviderErrorMessage.value = this._useNewWorkItemFieldService 
                    ? this._fieldService2.getFieldsErrorMessage()
                    : this._fieldService.getFieldsErrorMessage();
        }
    }

    private _getOperators(field: IFieldEntry): string[] {
        let operators = this._operators[field.referenceName];
        if (!operators) {
            operators = this._getLocalizedSupportedWiqlOperators(field);
            this._operators[field.referenceName] = operators;
        }

        return operators;
    }

    private async _updateAllowedValues(settings: ICriteriaSettingData) {
        settings.criteriaValues.ready.value = false;
        const field = this._useNewWorkItemFieldService 
                ? this._fieldService2.getFieldByReferenceName(settings.field)
                : this._fieldService.getFieldByReferenceName(settings.field);
        if (!field) {
            return;
        }

        const allowedValues = await this._getAllowedValues(field);

        // we might not have a list of allowed values
        if (allowedValues.length > 0) {
            settings.criteriaValues.value = allowedValues;
        } else {
            settings.criteriaValues.value = [];
        }

        settings.criteriaValues.ready.value = true;
    }

    private async _getAllowedValues(field: IFieldEntry): Promise<string[]> {
        const allowedValues = this._useNewWorkItemFieldService 
                ? await this._fieldService2.getAllowedValues(field.referenceName)
                : await this._fieldService.getAllowedValues(field.referenceName);
        if (!allowedValues) {
            this._dataProviderErrorMessage.value = this._useNewWorkItemFieldService 
                    ? this._fieldService2.getFieldAllowedValuesErrorMessage()
                    : this._fieldService.getFieldAllowedValuesErrorMessage();
        }

        return allowedValues || [];
    }

    private _validateCriteria() {
        const settings = this._rows.value;

        if (!this._validateUniqueCriteria(settings)) {
            this._validationErrorMessage.value = Resources.DuplicateFieldCriteriaErrorMessage;
        }
        // check that there are no empty selections
        else if (settings.findIndex(s => !s.field || !s.operator) >= 0) {
            // empty to show that the model isn't valid, but there is no error message to display
            this._validationErrorMessage.value = "";
        }
        // otherwise clear error message if necessary
        else if (this._validationErrorMessage.value !== undefined) {
            this._validationErrorMessage.value = undefined;
        }

        this._setIsValid();
        this._setIsDirty();
    }

    private _validateUniqueCriteria(settings: ICriteriaSettingData[]): boolean {
        const uniqueCriteriaSettings: { [fieldId: string]: string[] } = {};
        for (let i = 0, len = settings.length; i < len; i++) {
            const setting = settings[i];
            const operatorAndValue = setting.operator + setting.criteriaValue.value;
            if (!uniqueCriteriaSettings[setting.field]) {
                uniqueCriteriaSettings[setting.field] = [];
            } else if (setting.operator.length > 0 && uniqueCriteriaSettings[setting.field].indexOf(operatorAndValue) >= 0) {
                // found a duplicate
                return false;
            }

            uniqueCriteriaSettings[setting.field].push(operatorAndValue);
        }

        return true;
    }

    private _getDefaultCriteria(criteriaSetting?: ICriteriaSelectedSettingData): ICriteriaSettingData {
        const defaultSelectionIndex = 0;
        const defaultReferenceName = this._defaultField.referenceName;
        const operators = this._operators[defaultReferenceName];
        const operator = operators[defaultSelectionIndex];
        const id = newGuid();

        const criteriaSettingsData = {
            id,
            field: criteriaSetting?.field || defaultReferenceName,
            operator: criteriaSetting?.operator ? getLocalizedOperator(criteriaSetting.operator) : operator,
            criteriaValue: new ObservableValue(criteriaSetting?.criteriaValue.value || ""),
            fields: this._fieldListBoxItems,
            operators: new ObservableArray(this._getListBoxItems(operators)),
            criteriaValues: new ReadyableObservableArray([], false),
            fieldSelection: new DropdownSelection(),
            operatorSelection: new DropdownSelection(),
            hasError: new ObservableValue(false)
        };

        // update selections
        criteriaSettingsData.fieldSelection.select(defaultSelectionIndex);
        criteriaSettingsData.operatorSelection.select(defaultSelectionIndex);

        return criteriaSettingsData;
    }

    private _getListBoxItems(options: string[]): IListBoxItem[] {
        return options.map(option => {
            return { id: option, text: option } as IListBoxItem;
        });
    }

    /**
     * Return localized supported operators for the given field
     */
    private _getLocalizedSupportedWiqlOperators(field: IFieldEntry): string[] {
        if (field.isIdentity) {
            return CriteriaConstants.equalityOperators;
        }

        switch (field.type) {
            case FieldType.String:
                return CriteriaConstants.stringOperators;
            case FieldType.Integer:
            case FieldType.DateTime:
            case FieldType.Double:
                return CriteriaConstants.comparisonOperators;
            case FieldType.PlainText:
                return field.isTextQuerySupported ? CriteriaConstants.textWithTextSupportOperators : CriteriaConstants.textOperators;
            case FieldType.TreePath:
                return CriteriaConstants.treePathOperators;
            case FieldType.Boolean:
                return CriteriaConstants.equalityOperators;
            default:
                throw new Error("The given field is not supported");
        }
    }

    private _setIsValid() {
        const valid = this._validationErrorMessage.value === undefined;

        if (this._isValid.value !== valid) {
            this._isValid.value = valid;
        }
    }

    private _setIsDirty() {
        if (!this._originalCriteria) {
            return;
        }

        const currentCriteria = this._rows.value as ICriteriaSelectedSettingData[];
        let dirty = this._originalCriteria.length != currentCriteria.length;
        if (!dirty) {
            dirty = this._originalCriteria.findIndex((criteria, index) => !areCriteriaEqual(criteria, currentCriteria[index])) >= 0;
        }

        if (this._isDirty.value !== dirty) {
            this._isDirty.value = dirty;
        }
    }
}

export function areCriteriaEqual(a: ICriteriaSelectedSettingData, b: ICriteriaSelectedSettingData): boolean {
    return (
        a.criteriaValue.value === b.criteriaValue.value &&
        a.field === b.field &&
        getInvariantOperator(a.operator) === getInvariantOperator(b.operator)
    );
}

const loadingItem: IListBoxItem = {
    id: "loading",
    type: ListBoxItemType.Loading
};

const serviceName = "ICriteriaService";
Services.add(serviceName, { serviceFactory: CriteriaService });
